import React, { useContext, useState } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import i18next from 'i18next';
import { useDiceAnimSetting } from '../hooks/useDiceAnimSetting';
import { useDiceColorSetting } from '../hooks/useDiceColorSetting';
import UserContext from '../context/UserContext';
import { PLAYER_COLORS, colorFromUid } from '../utils/playerColors';
import { init } from '../utils/initFirebase';
import { useHistory } from 'react-router-dom';
import '../styles/settings.css';

init();
const db = firebase.firestore();

const SettingsRow = ({ label, description, children }) => (
  <div className="settingsRow">
    <div className="settingsRowLabel">
      <span>{label}</span>
      {description && <small>{description}</small>}
    </div>
    <div className="settingsRowControl">{children}</div>
  </div>
);

const Settings = () => {
  const { user, updateUser } = useContext(UserContext);
  const history = useHistory();
  const [animEnabled, toggleAnim] = useDiceAnimSetting();
  const [diceColor, setDiceColor] = useDiceColorSetting();

  const DICE_COLORS = [
    { value: '#f0a830', label: 'Or' },
    { value: '#c96b5e', label: 'Rouge' },
    { value: '#d4855a', label: 'Orange' },
    { value: '#d4b84a', label: 'Jaune' },
    { value: '#5ab87a', label: 'Vert' },
    { value: '#4aaa96', label: 'Turquoise' },
    { value: '#5a96c8', label: 'Bleu' },
    { value: '#8a6aaa', label: 'Violet' },
    { value: '#c45e96', label: 'Rose' },
    { value: '#e8e8e8', label: 'Blanc' },
    { value: '#909090', label: 'Argent' },
    { value: '#1b1d29', label: 'Nuit' },
    { value: 'rainbow', label: 'Arc-en-ciel' },
  ];
  const [theme, setTheme] = useState(user.theme || 'dark');
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'fr');
  const [playerColor, setPlayerColor] = useState(user.playerColor || colorFromUid(user.uid));

  const handleThemeChange = async () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    document.getElementsByTagName('body')[0].setAttribute('data-theme', newTheme);
    const updatedUser = { ...user, theme: newTheme };
    updateUser(updatedUser);
    await db.collection('users').doc(user.uid).set(updatedUser);
  };

  const handlePlayerColorChange = async (value) => {
    setPlayerColor(value);
    const updatedUser = { ...user, playerColor: value };
    updateUser(updatedUser);
    await db.collection('users').doc(user.uid).set(updatedUser);
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    i18next.changeLanguage(lang);
  };

  return (
    <div className="settingsPage">
      <button className="settingsBackBtn" onClick={() => history.goBack()}>
        ← {i18next.t('back')}
      </button>
      <div className="settingsProfile">
        {user.photoURL && <img src={user.photoURL} alt="avatar" className="settingsAvatar" />}
        <div className="settingsProfileInfo">
          <strong>{user.displayName || user.name}</strong>
          <span>{user.email}</span>
        </div>
      </div>

      <div className="settingsCard">
        <h2 className="settingsCardTitle">{i18next.t('settings appearance')}</h2>

        <SettingsRow label={i18next.t('settings theme dark')}>
          <button className={`settingsToggle ${theme === 'dark' ? 'on' : ''}`} onClick={handleThemeChange}>
            <span className="settingsToggleThumb" />
          </button>
        </SettingsRow>

        <div className="settingsDivider" />

        <SettingsRow label={i18next.t('settings language')}>
          <div className="settingsLangButtons">
            <button className={`langBtn ${language === 'fr' ? 'active' : ''}`} onClick={() => handleLanguageChange('fr')}>
              🇫🇷 FR
            </button>
            <button className={`langBtn ${language === 'en' ? 'active' : ''}`} onClick={() => handleLanguageChange('en')}>
              🇬🇧 EN
            </button>
          </div>
        </SettingsRow>

        <div className="settingsDivider" />

        <SettingsRow
          label={i18next.t('settings player color')}
          description={i18next.t('settings player color desc')}
        >
          <div className="diceColorPalette">
            {PLAYER_COLORS.map(c => (
              <button
                key={c.value}
                className={`diceColorSwatch ${playerColor === c.value ? 'active' : ''}`}
                style={{ backgroundColor: c.value }}
                title={c.label}
                onClick={() => handlePlayerColorChange(c.value)}
              />
            ))}
          </div>
        </SettingsRow>
      </div>

      <div className="settingsCard">
        <h2 className="settingsCardTitle">{i18next.t('settings dice')}</h2>
        <SettingsRow
          label={i18next.t('settings dice 3d')}
          description={i18next.t('settings dice 3d desc')}
        >
          <button className={`settingsToggle ${animEnabled ? 'on' : ''}`} onClick={toggleAnim}>
            <span className="settingsToggleThumb" />
          </button>
        </SettingsRow>

        <div className="settingsDivider" />

        <SettingsRow label={i18next.t('settings dice color')}>
          <div className="diceColorPalette">
            {DICE_COLORS.map(c => (
              <button
                key={c.value}
                className={`diceColorSwatch ${diceColor === c.value ? 'active' : ''} ${c.value === 'rainbow' ? 'rainbow' : ''} ${c.value === 'rainbow-pastel' ? 'rainbow-pastel' : ''}`}
                style={c.value !== 'rainbow' ? { backgroundColor: c.value } : {}}
                title={c.label}
                onClick={() => setDiceColor(c.value)}
              />
            ))}
          </div>
        </SettingsRow>
      </div>
    </div>
  );
};

export default Settings;
